import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

interface ActivityImageGalleryProps {
  images: string[];
  galleryTitle: string;
  imageAltPrefix: string;
  onExpandedChange?: (isExpanded: boolean) => void;
  cardCaption?: React.ReactNode;
  expandedCaption?: React.ReactNode;
}

const ActivityImageGallery: React.FC<ActivityImageGalleryProps> = ({
  images,
  galleryTitle,
  imageAltPrefix,
  onExpandedChange,
  cardCaption,
  expandedCaption,
}) => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const totalImages = images.length;

  useEffect(() => {
    if (currentIndex >= totalImages && totalImages > 0) {
      setCurrentIndex(0);
    }
  }, [totalImages, currentIndex]);

  useEffect(() => {
    onExpandedChange?.(isExpanded);
  }, [isExpanded, onExpandedChange]);

  const showPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + totalImages) % totalImages);
  };

  const showNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalImages);
  };

  const setExpanded = (value: boolean) => {
    setIsExpanded(value);
  };

  if (totalImages === 0) {
    return (
      <section className="max-w-5xl mx-auto mt-10 mb-10">
        <h2 className="text-2xl md:text-3xl font-semibold text-[#1C3F75] text-center mb-6">
          {galleryTitle}
        </h2>
        <div className="bg-white rounded-2xl shadow-md p-10 text-center">
          <p className="text-lg text-gray-600">{t('galleryComingSoon')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-5xl mx-auto mt-10 mb-10">
      <h2 className="text-2xl md:text-3xl font-semibold text-[#1C3F75] text-center mb-6">
        {galleryTitle}
      </h2>
      <div className="flex flex-col items-center">
        <div className="relative w-full max-w-sm md:max-w-md mx-auto">
          <div
            className="group bg-white rounded-2xl shadow-md overflow-hidden flex flex-col cursor-pointer"
            onClick={() => setExpanded(true)}
          >
            <div className="overflow-hidden bg-gray-100 flex items-center justify-center aspect-[3/4]">
              <img
                src={images[currentIndex]}
                alt={`${imageAltPrefix} ${currentIndex + 1}`}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-contain transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
            <div className="px-4 py-3 border-t border-gray-100 text-sm text-gray-700 flex items-center justify-between">
              {cardCaption ?? (
                <p className="text-gray-600 font-semibold">{t('activityGallery')}</p>
              )}
              <p className="text-xs text-gray-500 ml-auto">
                {currentIndex + 1} / {totalImages}
              </p>
            </div>
          </div>

          {totalImages > 1 && (
            <>
              <button
                type="button"
                onClick={showPrev}
                className="absolute top-1/2 -translate-y-1/2 -left-4 w-10 h-10 rounded-full bg-black/70 text-white flex items-center justify-center text-2xl shadow-lg hover:bg-black focus:outline-none"
                aria-label="Previous image"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={showNext}
                className="absolute top-1/2 -translate-y-1/2 -right-4 w-10 h-10 rounded-full bg-black/70 text-white flex items-center justify-center text-2xl shadow-lg hover:bg-black focus:outline-none"
                aria-label="Next image"
              >
                ›
              </button>
            </>
          )}
        </div>

        {isExpanded && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/70 text-white flex items-center justify-center text-xl shadow-lg hover:bg-black focus:outline-none"
              aria-label="Close image"
            >
              ✕
            </button>

            {totalImages > 1 && (
              <>
                <button
                  type="button"
                  onClick={showPrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 text-white flex items-center justify-center text-3xl shadow-lg hover:bg-white/40 focus:outline-none"
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={showNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 text-white flex items-center justify-center text-3xl shadow-lg hover:bg-white/40 focus:outline-none"
                  aria-label="Next image"
                >
                  ›
                </button>
              </>
            )}

            <div className="max-w-sm md:max-w-md w-full px-4 flex flex-col items-center">
              <img
                src={images[currentIndex]}
                alt={`${imageAltPrefix} ${currentIndex + 1}`}
                loading="lazy"
                decoding="async"
                className="w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl"
              />
              <div className="mt-4 text-center text-sm text-gray-200">
                {expandedCaption}
                <p className="text-xs mt-1">
                  {currentIndex + 1} / {totalImages}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ActivityImageGallery;
